import { useList } from '@/voc/use/use-list.js';
import { ref } from 'vue';

import { v4 as uuid } from 'uuid';

import { runAggregationOnce, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { hashCode } from '@/utils/string.utils';
import { EXPLORE_STATUSES, getExploreStatus } from '@/voc/helpers/permissions.helper';

import VocItemsAggregation from '@/voc/aggregations/voc-items/voc-items.aggregation';

const promiseStore = new Map();
const fetchingVocItems = {
    idea: ref(false),
    feedback: ref(false),
};

export function useVocItems ({ type = 'idea' } = {}) {
    type ??= 'idea';
    const aggregationId = `listen-voc-items-${type}-${uuid()}`;

    const {
        item: getVocItemById,
        items: vocItems,
        addItem: addVocItem,
        addItems: addVocItems,
        clearList: clearVocItems,
        keepAlive: keepAliveVocItems,
        updateItem: updateVocItem,
        removeItem: removeVocItem,
        cleanup: cleanupVocItems,
    } = useList(`listen-voc-items-${type}`);

    async function fetchVocItems ({ filters, revenue = false, store = {}, limit = 0, sortBy }) {
        const promiseKey = hashCode(`voc-items-${type}-${JSON.stringify(filters)}`);
        fetchingVocItems[type].value = true;

        if (promiseStore.get(promiseKey)) return promiseStore.get(promiseKey);

        const { accountDisplayName, revenueField, accountTypeField } = await loadCommonFields({ revenue, store });
        VocItemsAggregation.subProductArea = SUB_PRODUCT_AREAS.CAPTURE;

        promiseStore.set(
            promiseKey,
            runAggregationOnce(
                VocItemsAggregation,
                {
                    ...filters,
                    revenueField,
                    type,
                    expandInsights: getExploreStatus() === EXPLORE_STATUSES.READY,
                    limit,
                    sortBy,
                    accountDisplayName,
                    accountTypeField,
                },
                aggregationId,
            )
                .then((vocItems) => {
                    clearVocItems();
                    addVocItems(vocItems);
                })
                .catch((error) => {
                    clearVocItems();
                    throw error;
                })
                .finally(() => {
                    promiseStore.delete(promiseKey);
                    fetchingVocItems[type].value = false;
                }),
        );

        return promiseStore.get(promiseKey);
    }

    async function downloadCSV ({ filters, revenue = false, store = {}, limit = 0, sortBy }) {
        const { accountDisplayName, revenueField, accountTypeField } = await loadCommonFields({ revenue, store });

        VocItemsAggregation.subProductArea = SUB_PRODUCT_AREAS.CAPTURE;
        VocItemsAggregation.responseFormat = 'default';

        const columns = store.getters['vocItems/table/columns']
            ?.filter((column) => column.visible && column.downloadable !== false)
            ?.map((column) => ({
                field: column.csvProp || column.prop,
                label: column.csvLabel || column.label,
            }));

        return runAggregationOnce(
            VocItemsAggregation,
            {
                ...filters,
                revenueField,
                type,
                expandInsights: false,
                limit,
                sortBy,
                accountDisplayName,
                accountTypeField,
                isCsv: true,
                columns,
            },
            aggregationId,
        )
            .then((url) => {
                return url;
            })
            .catch((error) => {
                throw error;
            })
            .finally(() => {
                VocItemsAggregation.responseFormat = 'rows';
            });
    }

    async function loadCommonFields ({ revenue = false, store = {} }) {
        const { dispatch, state } = store;
        let accountDisplayName = state?.subscription?.settings?.accountDisplayName?.value || null;
        let revenueField = revenue ? state?.subscription?.settings?.revenue?.value : null;
        let accountTypeField = state?.subscription?.settings?.accountType?.value || null;

        const prereqPromises = [];

        if (revenue && !revenueField && dispatch) {
            prereqPromises.push(dispatch('subscription/loadSetting', { setting: 'revenue' }).catch((error) => error));
        }

        if (!accountDisplayName && dispatch) {
            prereqPromises.push(
                dispatch('subscription/loadSetting', { setting: 'accountDisplayName', retrieveMetadata: false }).catch(
                    (error) => error,
                ),
            );
        }

        if (!accountTypeField && dispatch) {
            prereqPromises.push(
                dispatch('subscription/loadSetting', { setting: 'accountType', retrieveMetadata: false }).catch(
                    (error) => error,
                ),
            );
        }

        if (prereqPromises.length) {
            await Promise.all(prereqPromises);

            accountDisplayName ??= state?.subscription?.settings?.accountDisplayName?.value || null;
            revenueField ??= revenue ? state?.subscription?.settings?.revenue?.value : null;
            accountTypeField ??= state?.subscription?.settings?.accountType?.value || null;
        }

        return { accountDisplayName, revenueField, accountTypeField };
    }

    return {
        fetchingVocItems: fetchingVocItems[type],
        vocItems,

        addVocItem,
        addVocItems,
        cleanupVocItems,
        clearVocItems,
        downloadCSV,
        fetchVocItems,
        getVocItemById,
        keepAliveVocItems,
        removeVocItem,
        updateVocItem,
    };
}
